import { configureStore, combineReducers, Middleware } from "@reduxjs/toolkit";
import createSagaMiddleware, { SagaMiddleware } from "redux-saga";
import { all } from "redux-saga/effects";

import authSaga from "./sagas/auth.saga";
import locationSaga from "./sagas/location.saga";
import uploadSaga from "./sagas/upload.saga";
import { orderSaga } from "./sagas/order.saga";

import authReducer from "../redux/slices/auth.slice";
import uploadReducer from "../redux/slices/uploadform.slice";
import uploadDataReducer from "../redux/slices/upload.slice";
import commonReducer from "../redux/slices/common.slice";
import locationReducer from "../redux/slices/location.slice";
import orderReducer from "../redux/slices/order.slice";
import brandReducer from "../redux/slices/brand.slice";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage"; // localStorage for web
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

/* ---------------------- Saga Middleware ---------------------- */
const sagaMiddleware = createSagaMiddleware();

/* ---------------------- Root Saga ---------------------- */
function* rootSaga() {
	yield all([authSaga(), locationSaga(), uploadSaga(), orderSaga()]);
}

/* ---------------------- Persist Config ---------------------- */
const uploadFormPersistConfig = {
	key: "uploadForm",
	storage,
	blacklist: ["dueDate"],
};

const persistConfig = {
	key: "arc-dropfile",
	storage,
	whitelist: ["auth", "common", "uploadForm"],
};

/* ---------------------- Persisted Reducer ---------------------- */
const persistedReducer = persistReducer(
	persistConfig,
	combineReducers({
		auth: authReducer,
		uploadForm: persistReducer(uploadFormPersistConfig, uploadReducer),
		common: commonReducer,
		location: locationReducer,
		upload: uploadDataReducer,
		order: orderReducer,
		brand: brandReducer,
	})
);

/* ---------------------- Store ---------------------- */
export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefault) =>
		getDefault({
			thunk: false,
			serializableCheck: false, // very important for redux-persist
		}).concat(sagaMiddleware),
});

/* ---------------------- Run Saga ---------------------- */
sagaMiddleware.run(rootSaga);

/* ---------------------- Persistor ---------------------- */
export const persistor = persistStore(store);

/* ---------------------- Types ---------------------- */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/* ---------------------- Hooks ---------------------- */
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
